import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Table, TableBody, TableHead, Toolbar, Typography, CircularProgress,
    Chip, Box, FormControl, InputLabel, Select, MenuItem, Button, Link,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { useQuery } from "@tanstack/react-query";
import { getSosEvents, resolveSosEvent, getSosFollowLink } from "../services/safetyService";
import { socketService } from "../services/socketService";
import { useCursorList } from "../hooks/useCursorList";
import LoadMoreBar from "../components/common/LoadMoreBar";
import { useToast } from "../lib/useToast";
import { getApiErrorMessage } from "../lib/toast";
import { useCan } from "../auth/Can";
import {
    StyledTableContainer, StyledTableCell, StyledTableRow, StyledPaper,
} from "../assets/styles/tableStyle";

const AUTO_REFRESH_MS = 15_000;

export interface SosEvent {
    id: number;
    riderUid: string;
    rideId: number | null;
    type: 'SOS' | 'TEST_DRILL' | 'CHECK_IN_HELP';
    status: 'ACTIVE' | 'RESOLVED' | 'CANCELLED';
    triggeredVia: string | null; // 'button' | 'shake' | 'check_in' | 'drill'
    lat: number | null;
    lng: number | null;
    contactsNotified: number;
    smsSent: number;
    followUrl: string | null;
    notifiedContacts: { name: string; phone: string; push: boolean; sms: boolean }[] | null;
    actionsTaken: string[] | null;
    resolvedAt: string | null;
    resolvedBy: string | null;
    resolutionNote: string | null;
    createdAt: string;
    updatedAt: string;
    rider: { name: string | null; phone: string };
    ride: {
        id: number;
        status: string;
        pickupLocation: string | null;
        dropoffLocation: string | null;
        driver: { name: string | null; phone: string } | null;
    } | null;
}

const COLUMNS = [
    'ID', 'Time', 'Type', 'Status', 'Rider', 'Ride', 'Trigger',
    'Location', 'Notified', 'Actions Taken', '',
];

const typeChipColor = (type: SosEvent['type']): 'error' | 'default' | 'warning' => {
    switch (type) {
        case 'SOS': return 'error';
        case 'CHECK_IN_HELP': return 'warning';
        default: return 'default';
    }
};

const StatusChip: React.FC<{ status: SosEvent['status'] }> = ({ status }) => {
    if (status === 'ACTIVE') {
        return (
            <Chip
                label="ACTIVE"
                color="error"
                size="small"
                sx={{
                    fontWeight: 700,
                    animation: 'sosPulse 1.2s ease-in-out infinite',
                    '@keyframes sosPulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.55 },
                        '100%': { opacity: 1 },
                    },
                }}
            />
        );
    }
    if (status === 'RESOLVED') return <Chip label="RESOLVED" color="success" size="small" variant="outlined" />;
    return <Chip label="CANCELLED" color="default" size="small" variant="outlined" />;
};

const SafetySos: React.FC = () => {
    const toast = useToast();
    const { can } = useCan();
    const canResolve = can('support:write');

    // Filters (changing them changes the query key -> resets & refetches the list)
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [typeFilter, setTypeFilter] = useState<string>("");

    const eventsList = useCursorList<SosEvent>({
        key: ['sos-events', { status: statusFilter, type: typeFilter }],
        pageSize: 25,
        // Auto-refreshing page: failures render inline below instead of stacking
        // a global error toast every poll cycle.
        meta: { suppressErrorToast: true },
        fetchPage: async ({ cursor, limit }) => {
            // Backend returns { events, nextCursor } — map it into the hook's page shape.
            const res = await getSosEvents({
                cursor,
                limit,
                status: statusFilter || undefined,
                type: typeFilter || undefined,
            });
            const events: SosEvent[] = res.data?.events ?? [];
            const nextCursor: number | null = res.data?.nextCursor ?? null;
            return { items: events, nextCursor, hasMore: nextCursor != null };
        },
    });

    // Count of ACTIVE real SOS events, independent of the table filters.
    const activeCountQuery = useQuery({
        queryKey: ['sos-events', 'active-count'],
        queryFn: async () => {
            const res = await getSosEvents({ status: 'ACTIVE', type: 'SOS', limit: 100 });
            return (res.data?.events ?? []).length as number;
        },
        refetchInterval: AUTO_REFRESH_MS,
        meta: { suppressErrorToast: true },
    });
    const activeCount = activeCountQuery.data ?? 0;

    // Auto-refresh the table every 15s while mounted (via ref so the interval
    // survives re-renders and is cleaned up on unmount).
    const refetchRef = useRef(eventsList.refetch);
    refetchRef.current = eventsList.refetch;
    const refetchCountRef = useRef(activeCountQuery.refetch);
    refetchCountRef.current = activeCountQuery.refetch;
    useEffect(() => {
        const timer = setInterval(() => refetchRef.current(), AUTO_REFRESH_MS);
        return () => clearInterval(timer);
    }, []);

    // Instant push: the backend emits to the 'admins' socket room on every real SOS
    // trigger/resolve. Polling above stays as the reconciliation path (sockets drop).
    const toastRef = useRef(toast);
    toastRef.current = toast;
    useEffect(() => {
        socketService.connect();
        const onSos = (payload: any) => {
            toastRef.current.error(
                `🚨 New SOS event #${payload?.sosEventId ?? ''} — rider needs attention`,
            );
            refetchRef.current();
            refetchCountRef.current();
        };
        const onSosUpdate = () => {
            refetchRef.current();
            refetchCountRef.current();
        };
        socketService.on('safety:sos', onSos);
        socketService.on('safety:sos:update', onSosUpdate);
        return () => {
            socketService.off('safety:sos', onSos);
            socketService.off('safety:sos:update', onSosUpdate);
        };
    }, []);

    const handleManualRefresh = () => {
        eventsList.refetch();
        activeCountQuery.refetch();
    };

    // Resolve dialog state
    const [resolveTarget, setResolveTarget] = useState<SosEvent | null>(null);
    const [resolveNote, setResolveNote] = useState<string>("");
    const [resolving, setResolving] = useState<boolean>(false);

    const openResolveDialog = (event: SosEvent) => {
        setResolveTarget(event);
        setResolveNote("");
    };

    // Admin "Live" click mints a FRESH follow token (public copies expire after 24h;
    // ops access must not). Tab is opened synchronously so popup blockers allow it,
    // then pointed at the minted URL.
    const handleOpenLive = async (eventId: number) => {
        const tab = window.open('about:blank', '_blank');
        try {
            const res = await getSosFollowLink(eventId);
            if (res.data?.url && tab) {
                tab.location.href = res.data.url;
            } else {
                tab?.close();
                toast.error("Couldn't open live tracking for this event");
            }
        } catch (e) {
            tab?.close();
            toast.error(getApiErrorMessage(e, "Couldn't open live tracking"));
        }
    };

    const handleResolve = async () => {
        if (!resolveTarget) return;
        setResolving(true);
        try {
            await resolveSosEvent(resolveTarget.id, resolveNote.trim() || undefined);
            toast.success(`SOS event #${resolveTarget.id} resolved`);
            setResolveTarget(null);
            handleManualRefresh();
        } catch (e) {
            toast.error(getApiErrorMessage(e, "Failed to resolve SOS event"));
        } finally {
            setResolving(false);
        }
    };

    const rows = eventsList.rows;
    const loading = eventsList.isLoading;

    const emptyMessage = useMemo(() => {
        if (statusFilter || typeFilter) return "No SOS events match the current filters.";
        return "No SOS events recorded.";
    }, [statusFilter, typeFilter]);

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="h6">Safety / SOS Events</Typography>
                    <Chip
                        icon={<ReportProblemIcon />}
                        label={`${activeCount} ACTIVE SOS`}
                        color="error"
                        variant={activeCount > 0 ? "filled" : "outlined"}
                        sx={activeCount > 0 ? {
                            fontWeight: 700,
                            animation: 'sosPulse 1.2s ease-in-out infinite',
                            '@keyframes sosPulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.6 },
                                '100%': { opacity: 1 },
                            },
                        } : { fontWeight: 600 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="RESOLVED">Resolved</MenuItem>
                            <MenuItem value="CANCELLED">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="SOS">SOS</MenuItem>
                            <MenuItem value="TEST_DRILL">Test Drill</MenuItem>
                            <MenuItem value="CHECK_IN_HELP">Check-in Help</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleManualRefresh}
                        disabled={eventsList.isFetching}
                    >
                        Refresh
                    </Button>
                </Box>
            </Toolbar>

            {(eventsList.isError || activeCountQuery.isError) && (
                <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
                    Couldn't load SOS events — retrying automatically every 15s. If this persists,
                    the backend may not have the safety endpoints yet (pending redeploy).
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <StyledTableContainer>
                    <Table stickyHeader sx={{ minWidth: 1100 }}>
                        <TableHead>
                            <StyledTableRow>
                                {COLUMNS.map((label, i) => (
                                    <StyledTableCell key={i}>{label}</StyledTableCell>
                                ))}
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((event) => (
                                <StyledTableRow key={event.id} hover>
                                    <StyledTableCell>{event.id}</StyledTableCell>
                                    <StyledTableCell>
                                        {new Date(event.createdAt).toLocaleString()}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={event.type.replace(/_/g, ' ')}
                                            color={typeChipColor(event.type)}
                                            size="small"
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <StatusChip status={event.status} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box>
                                            <Typography variant="body2" noWrap>{event.rider?.name || "-"}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {event.rider?.phone}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {event.ride ? (
                                            <Box>
                                                <Typography variant="body2" noWrap>Ride #{event.ride.id}</Typography>
                                                {event.ride.driver && (
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {event.ride.driver.name || "Driver"} · {event.ride.driver.phone}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : "-"}
                                    </StyledTableCell>
                                    <StyledTableCell>{event.triggeredVia || "-"}</StyledTableCell>
                                    <StyledTableCell>
                                        <Stack direction="row" spacing={1}>
                                            {event.lat != null && event.lng != null ? (
                                                <Link
                                                    href={`https://maps.google.com/?q=${event.lat},${event.lng}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Map
                                                </Link>
                                            ) : null}
                                            {event.rideId != null ? (
                                                <Link
                                                    component="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenLive(event.id);
                                                    }}
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    Live
                                                </Link>
                                            ) : null}
                                            {event.lat == null && event.rideId == null ? "-" : null}
                                        </Stack>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2" noWrap>
                                            {event.contactsNotified} push / {event.smsSent} sms
                                        </Typography>
                                        {(event.notifiedContacts ?? []).map((c) => (
                                            <Typography
                                                key={c.phone}
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                noWrap
                                            >
                                                {c.name} {c.phone}
                                                {c.push ? " · push" : ""}
                                                {c.sms ? " · sms" : ""}
                                                {!c.push && !c.sms ? " · not reached" : ""}
                                            </Typography>
                                        ))}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {event.actionsTaken?.length ? (
                                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                                {event.actionsTaken.map((action) => (
                                                    <Chip key={action} label={action} size="small" variant="outlined" />
                                                ))}
                                            </Stack>
                                        ) : "-"}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {event.status === 'ACTIVE' && canResolve && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="error"
                                                onClick={() => openResolveDialog(event)}
                                            >
                                                Resolve
                                            </Button>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                            {rows.length === 0 && !loading && (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={COLUMNS.length} align="center">
                                        <Typography color="textSecondary" sx={{ py: 3 }}>
                                            {emptyMessage}
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            )}

            <LoadMoreBar
                count={rows.length}
                hasMore={eventsList.hasMore}
                loading={eventsList.isLoadingMore}
                onLoadMore={eventsList.loadMore}
            />

            <Dialog
                open={Boolean(resolveTarget)}
                onClose={() => !resolving && setResolveTarget(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Resolve SOS Event #{resolveTarget?.id}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Rider: {resolveTarget?.rider?.name || "-"} ({resolveTarget?.rider?.phone})
                        {resolveTarget?.ride ? ` · Ride #${resolveTarget.ride.id}` : ""}
                    </Typography>
                    <TextField
                        label="Resolution note (optional)"
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        autoFocus
                        placeholder="e.g. Contacted rider — false alarm, ride completed safely."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveTarget(null)} disabled={resolving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResolve}
                        variant="contained"
                        color="error"
                        disabled={resolving}
                        startIcon={resolving ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        {resolving ? "Resolving…" : "Mark Resolved"}
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledPaper>
    );
};

export default SafetySos;
